import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  TextInput,
  ActivityIndicator,
  Platform,
  Alert,
} from 'react-native';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import WebScrollView from '../../components/WebScrollView';
import { supabase } from '../../lib/supabase';
import { SUPABASE_URL } from '../../lib/constants';
import { Send, Mail, MessageSquare, Search, RefreshCw, ArrowLeft, ArrowRight } from '../../lib/icons';
import { shadows } from '../../lib/styles';

type AnnouncementStatus = 'draft' | 'sending' | 'sent' | 'failed';

type AnnouncementRow = {
  id: string;
  title: string;
  subject: string;
  body: string;
  send_email: boolean;
  send_in_app: boolean;
  status: AnnouncementStatus;
  sent_at: string | null;
  created_at: string;
  announcement_audience_filters: {
    market_ids: string[];
    plans: string[];
    homeowner_ids: string[];
  } | {
    market_ids: string[];
    plans: string[];
    homeowner_ids: string[];
  }[] | null;
};

type Market = {
  id: string;
  name: string;
};

type Homeowner = {
  id: string;
  full_name: string | null;
  plan: string | null;
  home_count?: number;
  managed_home_count?: number;
};

type RecipientPreview = {
  homeowner_id: string;
  full_name: string | null;
  plan: string | null;
  market_names: string[] | null;
};

const PLAN_OPTIONS = ['free', 'managed'] as const;
const BUILDER_STEPS = ['Message', 'Audience', 'Preview & Confirm'] as const;

const getHomeownerPlanLabel = (homeowner: Homeowner) => {
  const homeCount = homeowner.home_count || 0;
  const managedHomeCount = homeowner.managed_home_count || 0;
  if (managedHomeCount > 0) {
    return homeCount > 1 ? `Premium (${managedHomeCount}/${homeCount})` : 'Premium';
  }
  return homeCount > 1 ? `Basic (0/${homeCount})` : 'Basic';
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const normalizeBodyToHtml = (value: string) => {
  if (!value) return '';
  const hasHtml = /<\/?[a-z][\s\S]*>/i.test(value);
  if (hasHtml) return value;
  return `<p>${escapeHtml(value).replace(/\n/g, '<br/>')}</p>`;
};

const stripHtml = (value: string) => {
  if (!value) return '';
  return value
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '')
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const normalizePreviewHtml = (value: string) =>
  value
    // Prevent pasted inline styles (especially giant line-height/font-size) from breaking preview fidelity.
    .replace(/\sstyle=(["']).*?\1/gi, '')
    .replace(/\sclass=(["']).*?\1/gi, '');

const statusColor = (status: AnnouncementStatus) => {
  if (status === 'sent') return '#16a34a';
  if (status === 'sending') return '#0369a1';
  if (status === 'failed') return '#dc2626';
  return '#64748b';
};

const AnnouncementsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const [testInAppLoading, setTestInAppLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'builder'>('list');
  const [builderStep, setBuilderStep] = useState(0);

  const [announcements, setAnnouncements] = useState<AnnouncementRow[]>([]);
  const [markets, setMarkets] = useState<Market[]>([]);
  const [homeowners, setHomeowners] = useState<Homeowner[]>([]);
  const [previewRecipients, setPreviewRecipients] = useState<RecipientPreview[]>([]);

  const [selectedAnnouncementId, setSelectedAnnouncementId] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sendEmailChannel, setSendEmailChannel] = useState(true);
  const [sendInAppChannel, setSendInAppChannel] = useState(true);
  const [selectedMarketIds, setSelectedMarketIds] = useState<string[]>([]);
  const [selectedPlans, setSelectedPlans] = useState<string[]>([]);
  const [selectedHomeownerIds, setSelectedHomeownerIds] = useState<string[]>([]);
  const [homeownerSearch, setHomeownerSearch] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [testHomeownerId, setTestHomeownerId] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const imageInputRef = useRef<HTMLInputElement | null>(null);

  const uploadAnnouncementImage = useCallback(async (file: File): Promise<string | null> => {
    try {
      setImageUploading(true);
      setError(null);
      const { data: authData } = await supabase.auth.getUser();
      if (!authData.user) throw new Error('Not authenticated');

      const timestamp = Date.now();
      const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const filePath = `${authData.user.id}/${timestamp}_${sanitizedName}`;

      const { error: uploadError } = await supabase.storage
        .from('announcement-images')
        .upload(filePath, file, { cacheControl: '3600', upsert: false });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('announcement-images')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (err: any) {
      console.error('Failed to upload announcement image:', err);
      setError(err.message || 'Failed to upload image');
      return null;
    } finally {
      setImageUploading(false);
    }
  }, []);

  // Stable ref so the TipTap editorProps closures can call the latest version
  const uploadAnnouncementImageRef = useRef(uploadAnnouncementImage);
  useEffect(() => { uploadAnnouncementImageRef.current = uploadAnnouncementImage; }, [uploadAnnouncementImage]);

  const editor = useEditor(
    Platform.OS === 'web'
      ? {
          extensions: [
            StarterKit,
            Link.configure({ openOnClick: false }),
            Placeholder.configure({ placeholder: 'Share announcement details here...' }),
            Image.configure({ inline: false, allowBase64: false }),
          ],
          content: '<p></p>',
          onUpdate: ({ editor: e }) => setBody(e.getHTML()),
          editorProps: {
            handlePaste: (_view, event) => {
              const items = event.clipboardData?.items;
              if (!items) return false;
              const imageItem = Array.from(items).find((item) => item.type.startsWith('image/'));
              if (!imageItem) return false;
              const file = imageItem.getAsFile();
              if (!file) return false;
              event.preventDefault();
              uploadAnnouncementImageRef.current(file).then((url) => {
                if (url) editorRef.current?.chain().focus().setImage({ src: url }).run();
              });
              return true;
            },
            handleDrop: (_view, event) => {
              const files = (event as DragEvent).dataTransfer?.files;
              if (!files || files.length === 0) return false;
              const imageFile = Array.from(files).find((f) => f.type.startsWith('image/'));
              if (!imageFile) return false;
              event.preventDefault();
              uploadAnnouncementImageRef.current(imageFile).then((url) => {
                if (url) editorRef.current?.chain().focus().setImage({ src: url }).run();
              });
              return true;
            },
          },
        }
      : null,
  );
  // Stable ref so callbacks can access the editor without adding it to dependency arrays.
  const editorRef = useRef(editor);
  useEffect(() => { editorRef.current = editor; }, [editor]);

  const previewHtml = useMemo(() => normalizePreviewHtml(body || '<p>-</p>'), [body]);

  const selectedAnnouncement = useMemo(
    () => announcements.find((item) => item.id === selectedAnnouncementId) || null,
    [announcements, selectedAnnouncementId],
  );
  const draftAnnouncements = useMemo(
    () => announcements.filter((item) => item.status === 'draft'),
    [announcements],
  );
  const previousAnnouncements = useMemo(
    () => announcements.filter((item) => item.status !== 'draft'),
    [announcements],
  );

  const filteredHomeowners = useMemo(() => {
    const search = homeownerSearch.trim().toLowerCase();
    if (!search) {
      return homeowners.slice(0, 30);
    }
    return homeowners
      .filter((homeowner) => (homeowner.full_name || 'Unnamed homeowner').toLowerCase().includes(search))
      .slice(0, 30);
  }, [homeowners, homeownerSearch]);

  const resetForm = useCallback(() => {
    setSelectedAnnouncementId(null);
    setTitle('');
    setSubject('');
    setBody('');
    editorRef.current?.commands.setContent('<p></p>');
    setSendEmailChannel(true);
    setSendInAppChannel(true);
    setSelectedMarketIds([]);
    setSelectedPlans([]);
    setSelectedHomeownerIds([]);
    setPreviewRecipients([]);
    setTestEmail('');
    setTestHomeownerId('');
    setHomeownerSearch('');
    setError(null);
    setSuccess(null);
    setBuilderStep(0);
  }, []);

  const hydrateFormFromAnnouncement = useCallback((announcement: AnnouncementRow) => {
    const rawFilters = announcement.announcement_audience_filters;
    const filters = Array.isArray(rawFilters) ? rawFilters[0] : rawFilters;
    const hydratedBody = normalizeBodyToHtml(announcement.body || '');
    setSelectedAnnouncementId(announcement.id);
    setTitle(announcement.title || '');
    setSubject(announcement.subject || '');
    setBody(hydratedBody);
    editorRef.current?.commands.setContent(hydratedBody);
    setSendEmailChannel(announcement.send_email);
    setSendInAppChannel(announcement.send_in_app);
    setSelectedMarketIds(filters?.market_ids || []);
    setSelectedPlans(filters?.plans || []);
    setSelectedHomeownerIds(filters?.homeowner_ids || []);
    setPreviewRecipients([]);
    setError(null);
    setSuccess(null);
  }, []);

  const startNewAnnouncement = () => {
    resetForm();
    setViewMode('builder');
  };

  const continueDraft = (announcement: AnnouncementRow) => {
    hydrateFormFromAnnouncement(announcement);
    setViewMode('builder');
    setBuilderStep(0);
  };

  const useAsTemplate = (announcement: AnnouncementRow) => {
    hydrateFormFromAnnouncement(announcement);
    setSelectedAnnouncementId(null);
    setViewMode('builder');
    setBuilderStep(0);
    setSuccess('Template loaded. Save to create a new draft.');
  };

  const closeBuilder = () => {
    setViewMode('list');
    setBuilderStep(0);
    setPreviewRecipients([]);
    setError(null);
    setSuccess(null);
  };

  const fetchInitialData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [announcementsResult, marketsResult, homeownersResult, homeownerHomesResult] = await Promise.all([
        supabase
          .from('announcements')
          .select(`
            id,
            title,
            subject,
            body,
            send_email,
            send_in_app,
            status,
            sent_at,
            created_at,
            announcement_audience_filters (
              market_ids,
              plans,
              homeowner_ids
            )
          `)
          .order('created_at', { ascending: false }),
        supabase
          .from('markets')
          .select('id, name')
          .eq('is_active', true)
          .order('name', { ascending: true }),
        supabase
          .from('profiles')
          .select('id, full_name')
          .eq('is_system_manager', false)
          .order('full_name', { ascending: true }),
        supabase
          .from('homes')
          .select('owner_id, plan')
          .not('owner_id', 'is', null),
      ]);

      if (announcementsResult.error) throw announcementsResult.error;
      if (marketsResult.error) throw marketsResult.error;
      if (homeownersResult.error) throw homeownersResult.error;
      if (homeownerHomesResult.error) throw homeownerHomesResult.error;

      const nextAnnouncements = (announcementsResult.data || []) as AnnouncementRow[];
      const planRollups = (homeownerHomesResult.data || []).reduce(
        (acc, home) => {
          if (!home.owner_id) return acc;
          if (!acc[home.owner_id]) {
            acc[home.owner_id] = { home_count: 0, managed_home_count: 0 };
          }
          acc[home.owner_id].home_count += 1;
          if (home.plan === 'managed') {
            acc[home.owner_id].managed_home_count += 1;
          }
          return acc;
        },
        {} as Record<string, { home_count: number; managed_home_count: number }>
      );
      const nextHomeowners = (homeownersResult.data || []).map((homeowner) => ({
        ...homeowner,
        plan: (planRollups[homeowner.id]?.managed_home_count || 0) > 0 ? 'managed' : 'free',
        home_count: planRollups[homeowner.id]?.home_count || 0,
        managed_home_count: planRollups[homeowner.id]?.managed_home_count || 0,
      })) as Homeowner[];
      setAnnouncements(nextAnnouncements);
      setMarkets((marketsResult.data || []) as Market[]);
      setHomeowners(nextHomeowners);

      if (selectedAnnouncementId) {
        const nextSelected = nextAnnouncements.find((item) => item.id === selectedAnnouncementId);
        if (nextSelected) {
          hydrateFormFromAnnouncement(nextSelected);
        } else {
          resetForm();
        }
      }
    } catch (err: any) {
      console.error('Failed to load announcements data:', err);
      setError(err.message || 'Failed to load announcements');
    } finally {
      setLoading(false);
    }
  }, [hydrateFormFromAnnouncement, resetForm, selectedAnnouncementId]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const validateForm = () => {
    const messageText = stripHtml(body);
    if (!title.trim()) return 'Title is required';
    if (!messageText) return 'Message body is required';
    if (!sendEmailChannel && !sendInAppChannel) return 'At least one delivery channel is required';
    if (sendEmailChannel && !subject.trim()) return 'Email subject is required when email channel is enabled';
    return null;
  };

  const upsertAudienceFilters = async (announcementId: string) => {
    const { error: filtersError } = await supabase
      .from('announcement_audience_filters')
      .upsert({
        announcement_id: announcementId,
        market_ids: selectedMarketIds,
        plans: selectedPlans,
        homeowner_ids: selectedHomeownerIds,
      }, {
        onConflict: 'announcement_id',
      });

    if (filtersError) throw filtersError;
  };

  const saveDraft = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return null;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const { data: authData } = await supabase.auth.getUser();
      const userId = authData.user?.id || null;

      if (selectedAnnouncementId) {
        const { error: updateError } = await supabase
          .from('announcements')
          .update({
            title: title.trim(),
            subject: subject.trim(),
            body: body.trim(),
            send_email: sendEmailChannel,
            send_in_app: sendInAppChannel,
            status: selectedAnnouncement?.status === 'sent' ? 'sent' : 'draft',
          })
          .eq('id', selectedAnnouncementId);

        if (updateError) throw updateError;

        await upsertAudienceFilters(selectedAnnouncementId);
        setSuccess('Announcement draft updated');
        await fetchInitialData();
        return selectedAnnouncementId;
      }

      const { data: inserted, error: insertError } = await supabase
        .from('announcements')
        .insert({
          title: title.trim(),
          subject: subject.trim(),
          body: body.trim(),
          send_email: sendEmailChannel,
          send_in_app: sendInAppChannel,
          status: 'draft',
          created_by: userId,
        })
        .select('id')
        .single();

      if (insertError) throw insertError;
      const createdAnnouncementId = inserted.id as string;

      await upsertAudienceFilters(createdAnnouncementId);
      setSelectedAnnouncementId(createdAnnouncementId);
      setSuccess('Announcement draft saved');
      await fetchInitialData();
      return createdAnnouncementId;
    } catch (err: any) {
      console.error('Failed to save announcement draft:', err);
      setError(err.message || 'Failed to save draft');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const refreshPreview = async (): Promise<RecipientPreview[]> => {
    try {
      setPreviewLoading(true);
      setError(null);

      const { data, error: previewError } = await supabase.rpc('resolve_announcement_recipients', {
        p_market_ids: selectedMarketIds,
        p_plans: selectedPlans,
        p_homeowner_ids: selectedHomeownerIds,
        p_limit: 500,
      });

      if (previewError) throw previewError;
      const resolved = (data || []) as RecipientPreview[];
      setPreviewRecipients(resolved);
      return resolved;
    } catch (err: any) {
      console.error('Failed to preview recipients:', err);
      setError(err.message || 'Failed to preview recipients');
      return [];
    } finally {
      setPreviewLoading(false);
    }
  };

  const invokeSendAnnouncementFunction = async (payload: Record<string, unknown>) => {
    const { data: sessionData } = await supabase.auth.getSession();
    const session = sessionData.session;
    if (!session?.access_token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${SUPABASE_URL}/functions/v1/send-announcement`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(payload),
    });

    const responseBody = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(responseBody?.error || 'Announcement function call failed');
    }

    return responseBody;
  };

  const sendTestEmail = async () => {
    const announcementId = await saveDraft();
    if (!announcementId) return;

    if (!testEmail.trim()) {
      setError('Enter a test email address');
      return;
    }

    try {
      setTestEmailLoading(true);
      setError(null);
      setSuccess(null);

      await invokeSendAnnouncementFunction({
        action: 'send_test_email',
        announcement_id: announcementId,
        test_email: testEmail.trim(),
      });

      setSuccess('Test email sent');
      await fetchInitialData();
    } catch (err: any) {
      console.error('Failed to send test email:', err);
      setError(err.message || 'Failed to send test email');
    } finally {
      setTestEmailLoading(false);
    }
  };

  const sendTestInApp = async () => {
    const announcementId = await saveDraft();
    if (!announcementId) return;

    if (!testHomeownerId) {
      setError('Select a test homeowner for in-app message');
      return;
    }

    try {
      setTestInAppLoading(true);
      setError(null);
      setSuccess(null);

      await invokeSendAnnouncementFunction({
        action: 'send_test_in_app',
        announcement_id: announcementId,
        test_homeowner_id: testHomeownerId,
      });

      setSuccess('Test in-app announcement sent');
      await fetchInitialData();
    } catch (err: any) {
      console.error('Failed to send test in-app announcement:', err);
      setError(err.message || 'Failed to send test in-app announcement');
    } finally {
      setTestInAppLoading(false);
    }
  };

  const sendAnnouncement = async () => {
    const announcementId = await saveDraft();
    if (!announcementId) return;

    const resolvedPreview = await refreshPreview();
    if (resolvedPreview.length === 0) {
      setError('No recipients match this audience. Adjust filters before sending.');
      return;
    }

    const confirmMessage = `Send this announcement to ${resolvedPreview.length} homeowners now?`;
    if (Platform.OS === 'web') {
      if (!window.confirm(confirmMessage)) return;
    } else {
      let allowSend = false;
      await new Promise<void>((resolve) => {
        Alert.alert('Confirm send', confirmMessage, [
          { text: 'Cancel', style: 'cancel', onPress: () => resolve() },
          {
            text: 'Send',
            style: 'destructive',
            onPress: () => {
              allowSend = true;
              resolve();
            },
          },
        ]);
      });
      if (!allowSend) return;
    }

    try {
      setSending(true);
      setError(null);
      setSuccess(null);

      const result = await invokeSendAnnouncementFunction({
        action: 'send',
        announcement_id: announcementId,
      });

      setSuccess(
        `Announcement sent to ${result.recipients} recipients. ` +
          `Email: ${result.email.success} sent / ${result.email.failed} failed. ` +
          `In-app: ${result.in_app.success} sent / ${result.in_app.failed} failed.`,
      );

      await fetchInitialData();
      setViewMode('list');
      setBuilderStep(0);
    } catch (err: any) {
      console.error('Failed to send announcement:', err);
      setError(err.message || 'Failed to send announcement');
    } finally {
      setSending(false);
    }
  };

  const toggleSelection = (values: string[], value: string, setValues: (next: string[]) => void) => {
    if (values.includes(value)) {
      setValues(values.filter((item) => item !== value));
    } else {
      setValues([...values, value]);
    }
  };

  const homeownerNameById = useMemo(() => {
    const map = new Map<string, string>();
    homeowners.forEach((homeowner) => map.set(homeowner.id, homeowner.full_name || 'Unnamed homeowner'));
    return map;
  }, [homeowners]);

  const renderMessageEditor = () => {
    if (Platform.OS !== 'web') {
      return (
        <TextInput
          style={[styles.input, styles.bodyInput]}
          value={stripHtml(body)}
          onChangeText={(value) => setBody(normalizeBodyToHtml(value))}
          placeholder="Share announcement details here..."
          placeholderTextColor="#94a3b8"
          multiline
          textAlignVertical="top"
        />
      );
    }

    return (
      <View style={styles.richEditorContainer}>
        <View style={styles.toolbarRow}>
          <Pressable
            style={[styles.toolbarButton, editor?.isActive('bold') && styles.toolbarButtonActive]}
            onPress={() => editor?.chain().focus().toggleBold().run()}
          >
            <Text style={[styles.toolbarButtonText, editor?.isActive('bold') && styles.toolbarButtonTextActive]}>B</Text>
          </Pressable>
          <Pressable
            style={[styles.toolbarButton, editor?.isActive('italic') && styles.toolbarButtonActive]}
            onPress={() => editor?.chain().focus().toggleItalic().run()}
          >
            <Text style={[styles.toolbarButtonText, editor?.isActive('italic') && styles.toolbarButtonTextActive]}>I</Text>
          </Pressable>
          <Pressable
            style={[styles.toolbarButton, editor?.isActive('strike') && styles.toolbarButtonActive]}
            onPress={() => editor?.chain().focus().toggleStrike().run()}
          >
            <Text style={[styles.toolbarButtonText, editor?.isActive('strike') && styles.toolbarButtonTextActive]}>S̶</Text>
          </Pressable>
          <Pressable
            style={[styles.toolbarButton, editor?.isActive('bulletList') && styles.toolbarButtonActive]}
            onPress={() => editor?.chain().focus().toggleBulletList().run()}
          >
            <Text style={[styles.toolbarButtonText, editor?.isActive('bulletList') && styles.toolbarButtonTextActive]}>• List</Text>
          </Pressable>
          <Pressable
            style={[styles.toolbarButton, editor?.isActive('orderedList') && styles.toolbarButtonActive]}
            onPress={() => editor?.chain().focus().toggleOrderedList().run()}
          >
            <Text style={[styles.toolbarButtonText, editor?.isActive('orderedList') && styles.toolbarButtonTextActive]}>1. List</Text>
          </Pressable>
          <Pressable
            style={[styles.toolbarButton, editor?.isActive('link') && styles.toolbarButtonActive]}
            onPress={() => {
              if (editor?.isActive('link')) {
                editor.chain().focus().unsetLink().run();
              } else {
                const url = window.prompt('Enter link URL');
                if (url?.trim()) editor?.chain().focus().setLink({ href: url.trim() }).run();
              }
            }}
          >
            <Text style={[styles.toolbarButtonText, editor?.isActive('link') && styles.toolbarButtonTextActive]}>
              {editor?.isActive('link') ? 'Unlink' : 'Link'}
            </Text>
          </Pressable>
          <Pressable
            style={[styles.toolbarButton, imageUploading && styles.disabledButton]}
            onPress={() => imageInputRef.current?.click()}
            disabled={imageUploading}
          >
            <Text style={styles.toolbarButtonText}>{imageUploading ? 'Uploading…' : '🖼 Image'}</Text>
          </Pressable>
        </View>
        {/* Hidden file input for image uploads */}
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={async (event) => {
            const file = event.target.files?.[0];
            if (!file) return;
            event.target.value = '';
            const url = await uploadAnnouncementImage(file);
            if (url) editorRef.current?.chain().focus().setImage({ src: url }).run();
          }}
        />
        <EditorContent editor={editor} className="announcement-rich-editor" />
        {imageUploading && (
          <View style={styles.imageUploadingBanner}>
            <ActivityIndicator size="small" color="#1d4ed8" />
            <Text style={styles.imageUploadingText}>Uploading image…</Text>
          </View>
        )}
        <Text style={styles.richEditorHint}>Supports bold, italic, lists, links, and images. Paste or drag images directly.</Text>
      </View>
    );
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#2563eb" />
      </View>
    );
  }

  return (
    <WebScrollView style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Announcements</Text>
        <Text style={styles.subtitle}>
          Build campaigns in steps: message, audience, then preview and confirm before sending.
        </Text>
      </View>

      {(error || success) && (
        <View style={[styles.messageBanner, error ? styles.errorBanner : styles.successBanner]}>
          <Text style={[styles.messageText, error ? styles.errorText : styles.successText]}>
            {error || success}
          </Text>
        </View>
      )}

      {viewMode === 'list' ? (
        <>
          <View style={styles.listActionRow}>
            <Pressable style={styles.secondaryButton} onPress={fetchInitialData}>
              <RefreshCw size={14} color="#334155" />
              <Text style={styles.secondaryButtonText}>Refresh</Text>
            </Pressable>
            <Pressable style={styles.sendNowButton} onPress={startNewAnnouncement}>
              <Send size={14} color="#ffffff" />
              <Text style={styles.sendNowButtonText}>Create New</Text>
            </Pressable>
          </View>

          <View style={styles.listSection}>
            <Text style={styles.panelTitle}>Drafts</Text>
            <View style={styles.draftList}>
              {draftAnnouncements.length === 0 ? (
                <Text style={styles.emptyText}>No drafts yet.</Text>
              ) : (
                draftAnnouncements.map((announcement) => (
                  <View key={announcement.id} style={styles.draftItem}>
                    <View style={styles.draftItemTop}>
                      <Text style={styles.draftTitle}>{announcement.title || 'Untitled draft'}</Text>
                      <View style={[styles.statusPill, { borderColor: statusColor(announcement.status) }]}>
                        <Text style={[styles.statusText, { color: statusColor(announcement.status) }]}>
                          {announcement.status}
                        </Text>
                      </View>
                    </View>
                    <Text style={styles.draftMeta}>{new Date(announcement.created_at).toLocaleString()}</Text>
                    <View style={styles.rowActions}>
                      <Pressable style={styles.primaryGhostButton} onPress={() => continueDraft(announcement)}>
                        <Text style={styles.primaryGhostButtonText}>Continue Draft</Text>
                      </Pressable>
                    </View>
                  </View>
                ))
              )}
            </View>
          </View>

          <View style={styles.listSection}>
            <Text style={styles.panelTitle}>Previous Announcements</Text>
            <View style={styles.compactList}>
              {previousAnnouncements.length === 0 ? (
                <Text style={styles.emptyText}>No previously sent announcements yet.</Text>
              ) : (
                previousAnnouncements.map((announcement) => (
                  <Pressable key={announcement.id} style={styles.compactAnnouncementRow} onPress={() => useAsTemplate(announcement)}>
                    <Text style={styles.compactAnnouncementTitle} numberOfLines={1}>
                      {announcement.title || 'Untitled announcement'}
                    </Text>
                    <View style={styles.compactMeta}>
                      <View style={[styles.statusPill, styles.compactStatusPill, { borderColor: statusColor(announcement.status) }]}>
                        <Text style={[styles.statusText, { color: statusColor(announcement.status) }]}>
                          {announcement.status}
                        </Text>
                      </View>
                      {announcement.send_email && (
                        <View style={styles.channelBadge}>
                          <Text style={styles.channelBadgeText}>Email</Text>
                        </View>
                      )}
                      {announcement.send_in_app && (
                        <View style={styles.channelBadge}>
                          <Text style={styles.channelBadgeText}>In-App</Text>
                        </View>
                      )}
                    </View>
                  </Pressable>
                ))
              )}
            </View>
          </View>
        </>
      ) : (
        <>
          <View style={styles.panel}>
            <View style={styles.panelHeaderRow}>
              <Text style={styles.panelTitle}>Create Announcement</Text>
              <Pressable style={styles.secondaryButton} onPress={closeBuilder}>
                <ArrowLeft size={14} color="#334155" />
                <Text style={styles.secondaryButtonText}>Back to List</Text>
              </Pressable>
            </View>
            <View style={styles.stepIndicatorRow}>
              {BUILDER_STEPS.map((step, idx) => {
                const isActive = idx === builderStep;
                const isComplete = idx < builderStep;
                return (
                  <View key={step} style={styles.stepPillWrap}>
                    <View
                      style={[
                        styles.stepPill,
                        isActive && styles.stepPillActive,
                        isComplete && styles.stepPillComplete,
                      ]}
                    >
                      <Text
                        style={[
                          styles.stepPillText,
                          (isActive || isComplete) && styles.stepPillTextActive,
                        ]}
                      >
                        {idx + 1}. {step}
                      </Text>
                    </View>
                  </View>
                );
              })}
            </View>
          </View>

          {builderStep === 0 && (
            <View style={styles.panel}>
              <Text style={styles.panelTitle}>Step 1: Message</Text>

              <Text style={styles.fieldLabel}>Title</Text>
              <TextInput
                style={styles.input}
                value={title}
                onChangeText={setTitle}
                placeholder="Spring maintenance offer"
                placeholderTextColor="#94a3b8"
              />

              <Text style={styles.fieldLabel}>Email Subject</Text>
              <TextInput
                style={styles.input}
                value={subject}
                onChangeText={setSubject}
                placeholder="Exclusive offer from HoneyDone"
                placeholderTextColor="#94a3b8"
              />

              <Text style={styles.fieldLabel}>Message Body</Text>
              {renderMessageEditor()}

              <Text style={styles.fieldLabel}>Channels</Text>
              <View style={styles.channelRow}>
                <Pressable
                  style={[styles.channelChip, sendEmailChannel && styles.channelChipSelected]}
                  onPress={() => setSendEmailChannel((prev) => !prev)}
                >
                  <Mail size={14} color={sendEmailChannel ? '#1d4ed8' : '#64748b'} />
                  <Text style={[styles.channelChipText, sendEmailChannel && styles.channelChipTextSelected]}>Email</Text>
                </Pressable>
                <Pressable
                  style={[styles.channelChip, sendInAppChannel && styles.channelChipSelected]}
                  onPress={() => setSendInAppChannel((prev) => !prev)}
                >
                  <MessageSquare size={14} color={sendInAppChannel ? '#1d4ed8' : '#64748b'} />
                  <Text style={[styles.channelChipText, sendInAppChannel && styles.channelChipTextSelected]}>In-App</Text>
                </Pressable>
              </View>
            </View>
          )}

          {builderStep === 1 && (
            <View style={styles.panel}>
              <Text style={styles.panelTitle}>Step 2: Audience</Text>

              <Text style={styles.fieldLabel}>Markets</Text>
              <View style={styles.chipWrap}>
                {markets.map((market) => {
                  const isSelected = selectedMarketIds.includes(market.id);
                  return (
                    <Pressable
                      key={market.id}
                      style={[styles.filterChip, isSelected && styles.filterChipSelected]}
                      onPress={() => toggleSelection(selectedMarketIds, market.id, setSelectedMarketIds)}
                    >
                      <Text style={[styles.filterChipText, isSelected && styles.filterChipTextSelected]}>
                        {market.name}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>

              <Text style={styles.fieldLabel}>Subscription Tiers</Text>
              <View style={styles.chipWrap}>
                {PLAN_OPTIONS.map((plan) => {
                  const isSelected = selectedPlans.includes(plan);
                  return (
                    <Pressable
                      key={plan}
                      style={[styles.filterChip, isSelected && styles.filterChipSelected]}
                      onPress={() => toggleSelection(selectedPlans, plan, setSelectedPlans)}
                    >
                      <Text style={[styles.filterChipText, isSelected && styles.filterChipTextSelected]}>
                        {plan}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>

              <Text style={styles.fieldLabel}>Specific Homeowners</Text>
              <View style={styles.searchBox}>
                <Search size={15} color="#64748b" />
                <TextInput
                  style={styles.searchInput}
                  value={homeownerSearch}
                  onChangeText={setHomeownerSearch}
                  placeholder="Search homeowners..."
                  placeholderTextColor="#94a3b8"
                />
              </View>
              <View style={styles.homeownerSelectionWrap}>
                {filteredHomeowners.map((homeowner) => {
                  const isSelected = selectedHomeownerIds.includes(homeowner.id);
                  return (
                    <Pressable
                      key={homeowner.id}
                      style={[styles.homeownerItem, isSelected && styles.homeownerItemSelected]}
                      onPress={() => toggleSelection(selectedHomeownerIds, homeowner.id, setSelectedHomeownerIds)}
                    >
                      <Text style={[styles.homeownerName, isSelected && styles.homeownerNameSelected]}>
                        {homeowner.full_name || 'Unnamed homeowner'}
                      </Text>
                      <Text style={[styles.homeownerPlan, isSelected && styles.homeownerNameSelected]}>
                        {getHomeownerPlanLabel(homeowner)}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>

              {selectedHomeownerIds.length > 0 && (
                <View style={styles.selectedPeopleWrap}>
                  {selectedHomeownerIds.map((id) => (
                    <View key={id} style={styles.selectedPersonChip}>
                      <Text style={styles.selectedPersonText}>{homeownerNameById.get(id) || id}</Text>
                      <Pressable onPress={() => toggleSelection(selectedHomeownerIds, id, setSelectedHomeownerIds)}>
                        <Text style={styles.selectedPersonRemove}>x</Text>
                      </Pressable>
                    </View>
                  ))}
                </View>
              )}
            </View>
          )}

          {builderStep === 2 && (
            <>
              <View style={styles.panel}>
                <View style={styles.panelHeaderRow}>
                  <Text style={styles.panelTitle}>Step 3: Preview & Confirm</Text>
                  <Pressable
                    style={styles.secondaryButton}
                    onPress={refreshPreview}
                    disabled={previewLoading}
                  >
                    {previewLoading ? (
                      <ActivityIndicator size="small" color="#334155" />
                    ) : (
                      <Text style={styles.secondaryButtonText}>Refresh Preview</Text>
                    )}
                  </Pressable>
                </View>
                <Text style={styles.previewCount}>
                  {previewRecipients.length} recipient{previewRecipients.length === 1 ? '' : 's'} matched
                </Text>
                <View style={styles.previewSummaryBox}>
                  <Text style={styles.previewSummaryTitle}>Message Preview</Text>
                  <Text style={styles.previewSummaryLine}>Title: {title || '-'}</Text>
                  <Text style={styles.previewSummaryLine}>Subject: {subject || '-'}</Text>
                  <Text style={styles.previewSummaryLine}>Channels: {sendEmailChannel ? 'Email' : ''}{sendEmailChannel && sendInAppChannel ? ' + ' : ''}{sendInAppChannel ? 'In-App' : ''}</Text>
                  {Platform.OS === 'web' ? (
                    <div
                      className="announcement-preview-html"
                      style={styles.previewSummaryHtml as any}
                      dangerouslySetInnerHTML={{ __html: previewHtml }}
                    />
                  ) : (
                    <Text style={styles.previewSummaryBody}>{stripHtml(body) || '-'}</Text>
                  )}
                </View>
                <View style={styles.previewList}>
                  {previewRecipients.length === 0 ? (
                    <Text style={styles.emptyText}>No recipients match the selected audience.</Text>
                  ) : (
                    previewRecipients.slice(0, 20).map((recipient) => (
                      <View key={recipient.homeowner_id} style={styles.previewItem}>
                        <Text style={styles.previewName}>{recipient.full_name || recipient.homeowner_id}</Text>
                        <Text style={styles.previewMeta}>
                          {recipient.plan || 'free'}
                          {recipient.market_names && recipient.market_names.length > 0
                            ? ` • ${recipient.market_names.join(', ')}`
                            : ' • No market'}
                        </Text>
                      </View>
                    ))
                  )}
                </View>
              </View>

              <View style={styles.panel}>
                <Text style={styles.panelTitle}>Optional Test Sends</Text>
                <Text style={styles.fieldLabel}>Email Test Recipient</Text>
                <TextInput
                  style={styles.input}
                  value={testEmail}
                  onChangeText={setTestEmail}
                  placeholder="test@example.com"
                  placeholderTextColor="#94a3b8"
                  keyboardType="email-address"
                  autoCapitalize="none"
                />
                <Pressable
                  style={[styles.primaryButton, testEmailLoading && styles.disabledButton]}
                  onPress={sendTestEmail}
                  disabled={testEmailLoading}
                >
                  {testEmailLoading ? (
                    <ActivityIndicator size="small" color="#ffffff" />
                  ) : (
                    <>
                      <Mail size={14} color="#ffffff" />
                      <Text style={styles.primaryButtonText}>Send Test Email</Text>
                    </>
                  )}
                </Pressable>

                <Text style={styles.fieldLabel}>In-App Test Homeowner</Text>
                <View style={styles.testHomeownerList}>
                  {homeowners.slice(0, 50).map((homeowner) => (
                    <Pressable
                      key={homeowner.id}
                      style={[
                        styles.testHomeownerItem,
                        testHomeownerId === homeowner.id && styles.testHomeownerItemSelected,
                      ]}
                      onPress={() => setTestHomeownerId(homeowner.id)}
                    >
                      <Text
                        style={[
                          styles.testHomeownerText,
                          testHomeownerId === homeowner.id && styles.testHomeownerTextSelected,
                        ]}
                      >
                        {homeowner.full_name || 'Unnamed homeowner'}
                      </Text>
                    </Pressable>
                  ))}
                </View>
                <Pressable
                  style={[styles.primaryButton, testInAppLoading && styles.disabledButton]}
                  onPress={sendTestInApp}
                  disabled={testInAppLoading}
                >
                  {testInAppLoading ? (
                    <ActivityIndicator size="small" color="#ffffff" />
                  ) : (
                    <>
                      <MessageSquare size={14} color="#ffffff" />
                      <Text style={styles.primaryButtonText}>Send Test In-App Message</Text>
                    </>
                  )}
                </Pressable>
              </View>
            </>
          )}

          <View style={styles.footerActions}>
            <Pressable
              style={[styles.secondaryButton, saving && styles.disabledButton]}
              onPress={saveDraft}
              disabled={saving}
            >
              {saving ? (
                <ActivityIndicator size="small" color="#334155" />
              ) : (
                <Text style={styles.secondaryButtonText}>Save Draft</Text>
              )}
            </Pressable>

            {builderStep > 0 && (
              <Pressable
                style={styles.secondaryButton}
                onPress={() => setBuilderStep((prev) => Math.max(prev - 1, 0))}
              >
                <ArrowLeft size={14} color="#334155" />
                <Text style={styles.secondaryButtonText}>Back</Text>
              </Pressable>
            )}

            {builderStep < BUILDER_STEPS.length - 1 ? (
              <Pressable
                style={styles.sendNowButton}
                onPress={async () => {
                  const id = await saveDraft();
                  if (!id) return;
                  if (builderStep === 1) {
                    await refreshPreview();
                  }
                  setBuilderStep((prev) => Math.min(prev + 1, BUILDER_STEPS.length - 1));
                }}
              >
                <Text style={styles.sendNowButtonText}>Next</Text>
                <ArrowRight size={14} color="#ffffff" />
              </Pressable>
            ) : (
              <Pressable
                style={[
                  styles.sendNowButton,
                  (sending || selectedAnnouncement?.status === 'sent') && styles.disabledButton,
                ]}
                onPress={sendAnnouncement}
                disabled={sending || selectedAnnouncement?.status === 'sent'}
              >
                {sending ? (
                  <ActivityIndicator size="small" color="#ffffff" />
                ) : (
                  <>
                    <Send size={15} color="#ffffff" />
                    <Text style={styles.sendNowButtonText}>
                      {selectedAnnouncement?.status === 'sent' ? 'Already Sent' : 'Send Announcement'}
                    </Text>
                  </>
                )}
              </Pressable>
            )}
          </View>
        </>
      )}
    </WebScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    backgroundColor: '#f8fafc',
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f8fafc',
  },
  header: {
    marginBottom: 16,
    gap: 6,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    color: '#0f172a',
  },
  subtitle: {
    fontSize: 14,
    color: '#475569',
    lineHeight: 20,
  },
  messageBanner: {
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 14,
    borderWidth: 1,
  },
  errorBanner: {
    backgroundColor: '#fef2f2',
    borderColor: '#fecaca',
  },
  successBanner: {
    backgroundColor: '#f0fdf4',
    borderColor: '#bbf7d0',
  },
  messageText: {
    fontSize: 13,
    fontWeight: '500',
  },
  errorText: {
    color: '#b91c1c',
  },
  successText: {
    color: '#166534',
  },
  panel: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    padding: 14,
    marginBottom: 14,
    gap: 10,
    ...shadows.sm,
  },
  panelHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  panelTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#0f172a',
  },
  panelActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  listActionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    marginBottom: 14,
  },
  listSection: {
    marginBottom: 14,
    gap: 10,
  },
  draftList: {
    gap: 8,
  },
  compactList: {
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 10,
    overflow: 'hidden',
  },
  compactAnnouncementRow: {
    minHeight: 42,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
    paddingHorizontal: 10,
    paddingVertical: 7,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  compactAnnouncementTitle: {
    flex: 1,
    fontSize: 13,
    color: '#0f172a',
    fontWeight: '600',
  },
  compactMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  compactStatusPill: {
    paddingHorizontal: 6,
    paddingVertical: 1,
  },
  channelBadge: {
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 999,
    paddingHorizontal: 6,
    paddingVertical: 2,
    backgroundColor: '#f8fafc',
  },
  channelBadgeText: {
    fontSize: 11,
    color: '#475569',
    fontWeight: '600',
  },
  draftItem: {
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 10,
    padding: 10,
    gap: 6,
    backgroundColor: '#ffffff',
  },
  draftItemSelected: {
    borderColor: '#2563eb',
    backgroundColor: '#eff6ff',
  },
  rowActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 2,
  },
  stepIndicatorRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 6,
  },
  stepPillWrap: {
    flexShrink: 0,
  },
  stepPill: {
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    paddingHorizontal: 11,
    paddingVertical: 6,
    backgroundColor: '#ffffff',
  },
  stepPillActive: {
    borderColor: '#2563eb',
    backgroundColor: '#dbeafe',
  },
  stepPillComplete: {
    borderColor: '#93c5fd',
    backgroundColor: '#eff6ff',
  },
  stepPillText: {
    fontSize: 12,
    color: '#475569',
    fontWeight: '600',
  },
  stepPillTextActive: {
    color: '#1e40af',
  },
  draftItemTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
  },
  draftTitle: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
    color: '#0f172a',
  },
  draftMeta: {
    fontSize: 12,
    color: '#64748b',
  },
  statusPill: {
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  statusText: {
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  fieldLabel: {
    fontSize: 12,
    color: '#334155',
    fontWeight: '600',
    marginTop: 2,
  },
  input: {
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
    fontSize: 14,
    color: '#0f172a',
    backgroundColor: '#ffffff',
  },
  bodyInput: {
    minHeight: 140,
  },
  richEditorContainer: {
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#ffffff',
  },
  toolbarRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 8,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
    backgroundColor: '#f8fafc',
  },
  toolbarButton: {
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: '#ffffff',
  },
  toolbarButtonActive: {
    borderColor: '#93c5fd',
    backgroundColor: '#dbeafe',
  },
  toolbarButtonText: {
    fontSize: 12,
    color: '#334155',
    fontWeight: '700',
  },
  toolbarButtonTextActive: {
    color: '#1d4ed8',
  },
  richEditorHint: {
    fontSize: 11,
    color: '#64748b',
    paddingHorizontal: 10,
    paddingBottom: 10,
  },
  imageUploadingBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: '#eff6ff',
    borderTopWidth: 1,
    borderTopColor: '#bfdbfe',
  },
  imageUploadingText: {
    fontSize: 12,
    color: '#1d4ed8',
    fontWeight: '600',
  },
  channelRow: {
    flexDirection: 'row',
    gap: 8,
  },
  channelChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 999,
    paddingHorizontal: 12,
    paddingVertical: 7,
    backgroundColor: '#ffffff',
  },
  channelChipSelected: {
    borderColor: '#93c5fd',
    backgroundColor: '#eff6ff',
  },
  channelChipText: {
    fontSize: 12,
    color: '#475569',
    fontWeight: '600',
  },
  channelChipTextSelected: {
    color: '#1d4ed8',
  },
  chipWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  filterChip: {
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 999,
    paddingHorizontal: 12,
    paddingVertical: 7,
  },
  filterChipSelected: {
    borderColor: '#93c5fd',
    backgroundColor: '#eff6ff',
  },
  filterChipText: {
    fontSize: 12,
    color: '#334155',
    fontWeight: '600',
  },
  filterChipTextSelected: {
    color: '#1d4ed8',
  },
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 8,
    paddingHorizontal: 10,
    backgroundColor: '#ffffff',
  },
  searchInput: {
    flex: 1,
    height: 36,
    fontSize: 14,
    color: '#0f172a',
  },
  homeownerSelectionWrap: {
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 8,
    maxHeight: 210,
    overflow: 'hidden',
  },
  homeownerItem: {
    paddingHorizontal: 10,
    paddingVertical: 9,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
  },
  homeownerItemSelected: {
    backgroundColor: '#eff6ff',
  },
  homeownerName: {
    flex: 1,
    fontSize: 13,
    color: '#0f172a',
  },
  homeownerNameSelected: {
    color: '#1d4ed8',
    fontWeight: '600',
  },
  homeownerPlan: {
    fontSize: 12,
    color: '#64748b',
  },
  selectedPeopleWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  selectedPersonChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    borderRadius: 999,
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: '#bfdbfe',
    backgroundColor: '#eff6ff',
  },
  selectedPersonText: {
    fontSize: 12,
    color: '#1d4ed8',
    fontWeight: '600',
  },
  selectedPersonRemove: {
    fontSize: 12,
    color: '#1e3a8a',
    fontWeight: '700',
    lineHeight: 14,
  },
  previewCount: {
    fontSize: 12,
    color: '#64748b',
    fontWeight: '600',
  },
  previewList: {
    gap: 8,
  },
  previewSummaryBox: {
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 9,
    backgroundColor: '#f8fafc',
    gap: 4,
  },
  previewSummaryTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#0f172a',
  },
  previewSummaryLine: {
    fontSize: 12,
    color: '#334155',
  },
  previewSummaryBody: {
    marginTop: 4,
    fontSize: 13,
    color: '#0f172a',
    lineHeight: 20,
  },
  previewSummaryHtml: {
    marginTop: 6,
    fontSize: 13,
    color: '#0f172a',
    lineHeight: 20,
  },
  previewItem: {
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  previewName: {
    fontSize: 13,
    fontWeight: '600',
    color: '#0f172a',
  },
  previewMeta: {
    fontSize: 12,
    color: '#64748b',
    marginTop: 2,
  },
  testHomeownerList: {
    maxHeight: 180,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 8,
    overflow: 'hidden',
  },
  testHomeownerItem: {
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  testHomeownerItemSelected: {
    backgroundColor: '#eff6ff',
  },
  testHomeownerText: {
    fontSize: 13,
    color: '#0f172a',
  },
  testHomeownerTextSelected: {
    color: '#1d4ed8',
    fontWeight: '600',
  },
  footerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
    marginBottom: 12,
  },
  primaryButton: {
    marginTop: 4,
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: '#2563eb',
  },
  primaryButtonText: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '700',
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 7,
    backgroundColor: '#ffffff',
  },
  secondaryButtonText: {
    fontSize: 12,
    color: '#334155',
    fontWeight: '600',
  },
  primaryGhostButton: {
    borderWidth: 1,
    borderColor: '#93c5fd',
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 7,
    backgroundColor: '#eff6ff',
  },
  primaryGhostButtonText: {
    fontSize: 12,
    color: '#1d4ed8',
    fontWeight: '700',
  },
  sendNowButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 9,
    backgroundColor: '#1d4ed8',
  },
  sendNowButtonText: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '700',
  },
  disabledButton: {
    opacity: 0.65,
  },
  emptyText: {
    fontSize: 13,
    color: '#64748b',
  },
});

export default AnnouncementsTab;
